import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ToastController } from '@ionic/angular';
import { BaseClass } from 'src/app/shared/classes/base';
import { ProcessConfig, QuestionConfig, QuestionTypeEnum } from 'src/app/shared/interfaces/process-config.interface';
import { ProcessStatusEnum, ProcessTypeEnum } from 'src/app/shared/interfaces/process.interface';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { dateFormatTextual } from 'src/app/shared/utils/dateFormat';
import { Goal, GoalStatusEnum } from '../../_interfaces/goal.interface';

@Component({
  selector: 'app-user-goals-list',
  templateUrl: './user-goals-list.component.html',
  styleUrls: ['./user-goals-list.component.scss'],
})
export class UserGoalsListComponent extends BaseClass implements OnInit, OnChanges {

  @Input() processType: ProcessTypeEnum = ProcessTypeEnum.GoalInputs;
  @Input() goals: Array<Goal>;
  @Input() goalsInputConfig: ProcessConfig;
  @Input() processStatus: ProcessStatusEnum = ProcessStatusEnum.NotEnabled;
  @Input() isConsentValid: boolean = false;
  @Input() canEditGoals: boolean = false;
  @Input() canApproveGoals: boolean = false;
  @Input() canDeleteGoal: boolean = false;
  @Input() showValidatedBadge: boolean = false;
  @Output() approve = new EventEmitter<{ goal: Goal }>();
  @Output() refuse = new EventEmitter<{ goal: Goal }>();
  @Output() edit = new EventEmitter<{ goal: Goal }>();
  @Output() remove = new EventEmitter<{ goal: Goal }>();
  @Output() delete = new EventEmitter<{ goal: Goal }>();
  @Output() viewGoalProgress = new EventEmitter<{ goal: Goal }>();

  ProcessStatusEnum = ProcessStatusEnum;
  GoalStatusEnum = GoalStatusEnum;
  public goalsFilteredQuestions: Array<QuestionConfig> = [];
  public goalsFormArray: FormArray;
  public refusalPendingGoalIds: { [key: string]: boolean } = {};

  constructor(
    protected _toastController: ToastController,
    protected _translationService: TranslationService,
    private _formBuilder: FormBuilder,
  ) {
    super(_toastController, _translationService);
    this.goalsFormArray = this._formBuilder.array([]);
  }

  ngOnInit() {
    this.updateGoalsQuestions();
    this.updateGoalsForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['goalsInputConfig'] || (changes['goals'])) {
      this.updateGoalsQuestions();
      this.updateGoalsForm();
    }
  }

  private updateGoalsQuestions() {
    this.goalsFilteredQuestions = this.goalsInputConfig?.
      questions?.filter(q => q.typeConfig.type !== QuestionTypeEnum.Description
        && q.typeConfig.type !== QuestionTypeEnum.Title
      ) || [];
  }

  public getLabelText(questionConfig: QuestionConfig, property: string): string {
    if (!questionConfig) return '';
    switch (questionConfig.typeConfig.type) {
      case QuestionTypeEnum.Weight:
        return property ? property + '%' : '';
      case QuestionTypeEnum.Deadline || QuestionTypeEnum.InitialDeadline || QuestionTypeEnum.Date:
        return dateFormatTextual(property, this.selectedLanguage) || '';
      case QuestionTypeEnum.Selection:
        let index = questionConfig.typeConfig?.possibleValues?.findIndex(o => o === property);
        return index !== -1 ? questionConfig.typeConfig?.possibleValuesGlobalized?.[this.selectedLanguage]?.[index] : '';
      default:
        return property || '';
    }
  }

  private updateGoalsForm() {
    this.goalsFormArray.clear();

    if (this.goals && this.goals.length > 0) {
      this.goals.forEach(goal => {
        this.goalsFormArray.push(this.createGoalFormGroup(goal));
      });
    }
  }

  public onApprove(goal: Goal): void {
    this.clearRefuseMode(goal);
    goal.approved = true;
    this.approve.emit({ goal });
  }

  public onRefuse(goal: Goal): void {
    this.clearRefuseMode(goal);
    goal.approved = false;
    this.refuse.emit({ goal });
  }

  public startRefuseMode(goal: Goal): void {
    const key = this.getGoalKey(goal);
    this.refusalPendingGoalIds[key] = true;
    goal.approved = false;
  }

  public cancelRefuseMode(goal: Goal): void {
    goal.approved = undefined;
    this.clearRefuseMode(goal);
  }

  public confirmRefuse(goal: Goal): void {
    this.onRefuse(goal);
  }

  public isRefuseMode(goal: Goal): boolean {
    return !!this.refusalPendingGoalIds[this.getGoalKey(goal)];
  }

  public isPersistedRefused(goal: Goal): boolean {
    return goal?.status === GoalStatusEnum.Rejected || goal?.status === GoalStatusEnum.ReviewRejected;
  }

  private clearRefuseMode(goal: Goal): void {
    const key = this.getGoalKey(goal);
    if (this.refusalPendingGoalIds[key]) {
      delete this.refusalPendingGoalIds[key];
    }
  }

  private getGoalKey(goal: Goal): string {
    return `${goal?.id ?? ''}-${goal?.cycleParticipantId ?? ''}-${goal?.creationUserId ?? ''}`;
  }

  public getBackGroundColor(goal: Goal): any {
    switch (goal?.status) {
      case GoalStatusEnum.Draft:
        return 'var(--ion-color-lightgray)';
      case GoalStatusEnum.WaitingForApproval:
        return 'rgba(var(--ion-color-light-warning-rgb), 0.8)';
      case GoalStatusEnum.Rejected:
        return 'rgba(var(--ion-color-light-danger-rgb), 0.8)';
      case GoalStatusEnum.Approved:
        return 'rgba(var(--ion-color-light-success-rgb), 0.8)';
      case GoalStatusEnum.DraftForReview:
        return 'rgba(var(--ion-color-light-warning-rgb), 0.8)';
      case GoalStatusEnum.SentForReview:
        return 'rgba(var(--ion-color-light-warning-rgb), 0.8)';
      case GoalStatusEnum.ReviewRejected:
        return 'rgba(var(--ion-color-light-danger-rgb), 0.8)';
      case GoalStatusEnum.ReviewAccepted:
        return 'rgba(var(--ion-color-light-success-rgb), 0.8)';
      default:
        return null;
    }
  }
  public getColor(goal: Goal): any {
    switch (goal?.status) {
      case GoalStatusEnum.Draft:
        return null;
      case GoalStatusEnum.WaitingForApproval:
        return '#000000';
      case GoalStatusEnum.Rejected:
        return '#000000';
      case GoalStatusEnum.Approved:
        return '#000000';
      case GoalStatusEnum.DraftForReview:
        return '#000000';
      case GoalStatusEnum.SentForReview:
        return '#000000';
      case GoalStatusEnum.ReviewRejected:
        return '#000000';
      case GoalStatusEnum.ReviewAccepted:
        return '#000000';
      default:
        return '#000000';
    }
  }

  getTypeLabel(goal: Goal): string {
    const anyGoal = goal as any;

    return anyGoal?.typeDescription
      || anyGoal?.typeName
      || anyGoal?.typeLabel
      || (goal?.type !== undefined ? String(goal.type) : '-');
  }

  getStatusLabel(goal: Goal): string {
    switch (goal?.status) {
      case GoalStatusEnum.Draft:
        return 'goalsStatus.draft';
      case GoalStatusEnum.WaitingForApproval:
        return 'goalsStatus.waitingForApproval';
      case GoalStatusEnum.Rejected:
        return 'goalsStatus.rejected';
      case GoalStatusEnum.Approved:
        return 'goalsStatus.approved';
      case GoalStatusEnum.DraftForReview:
        return 'goalsStatus.draftForReview';
      case GoalStatusEnum.SentForReview:
        return 'goalsStatus.sentForReview';
      case GoalStatusEnum.ReviewRejected:
        return 'goalsStatus.reviewRejected';
      case GoalStatusEnum.ReviewAccepted:
        return 'goalsStatus.reviewAccepted';
      default:
        return '-';
    }
  }

  getTitle(goal: AbstractControl): string {
    let titleIndex = this.goalsInputConfig?.questions?.findIndex(q => q.typeConfig.type === QuestionTypeEnum.Title);
    if (titleIndex !== undefined && titleIndex !== -1) {
      return goal['field' + (titleIndex + 1)] || '-';
    }
    return '-';
  }

  getDescription(goal: AbstractControl): string {
    let descriptionIndex = this.goalsInputConfig?.questions?.findIndex(q => q.typeConfig.type === QuestionTypeEnum.Description);
    if (descriptionIndex !== undefined && descriptionIndex !== -1) {
      return goal['field' + (descriptionIndex + 1)] || '-';
    }
    return '-';
  }

  public hasActionButtons(goal: Goal): boolean {
    return (this.canEditGoals && this.processType === ProcessTypeEnum.GoalInputs) ||
      (this.processType === ProcessTypeEnum.GoalInputValidation || this.processType === ProcessTypeEnum.GoalsReviewValidation) ||
      (this.canEditGoals && this.processType === ProcessTypeEnum.GoalsReview) ||
      (this.showValidatedBadge && goal?.status === GoalStatusEnum.ReviewAccepted);
  }

  private createGoalFormGroup(goal: Goal): FormGroup {
    return this._formBuilder.group({
      id: [goal.id],
      cycleParticipantId: [goal.cycleParticipantId],
      creationUserId: [goal.creationUserId],
      title: [goal.title],
      description: [goal.description],
      weight: [goal.weight],
      managerComment: [goal.managerComment],
      deadline: [goal.deadline],
      initialDeadline: [goal.initialDeadline],
      type: [goal.type],
      processType: [goal.processType],
      status: [goal.status],
      field1: [goal.field1],
      field2: [goal.field2],
      field3: [goal.field3],
      field4: [goal.field4],
      field5: [goal.field5],
      field6: [goal.field6],
      field7: [goal.field7],
      field8: [goal.field8],
      field9: [goal.field9],
      field10: [goal.field10]
    });
  }
}
